/**
 * UserManagementConfirmRevokeAll Component
 *
 * Confirmation dialog for revoking all myRSM access from a user.
 * Displays a critical confirmation prompt with user information before
 * removing all tool-role assignments.
 *
 * Features:
 * - Material-UI Dialog with alertdialog role for critical confirmation
 * - Displays user's full name and email in bold for verification
 * - Cancel and Remove user action buttons
 * - Internationalization support (English and Spanish)
 * - WCAG 2.1 AA accessibility compliance with proper ARIA attributes
 * - Escape key and backdrop click support for closing
 * - Focus management handled automatically by Dialog component
 */

import React from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { tokens } from 'src/styles/materialTheme'
import ResponsiveModal from '../../rsmCoreComponents/components/ResponsiveModal'
import { Styles } from '../../types'

/**
 * Props interface for UserManagementConfirmRevokeAll component
 */
interface UserManagementConfirmRevokeAllProps {
  /** Controls dialog visibility */
  open: boolean
  /** Callback invoked when dialog should close without confirming */
  onClose: () => void
  /** Callback invoked when user confirms revoke action */
  onConfirm: () => void
  /** User's first name for display in confirmation message */
  userFirstName: string
  /** User's last name for display in confirmation message */
  userLastName: string
  /** User's email address for display in confirmation message */
  userEmail: string
}

const styles: Styles = {
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: '2rem 2.5rem 2rem 2.5rem',
    backgroundColor: 'white',
  },
  title: {
    fontFamily: 'Prelo, sans-serif',
    fontSize: '1.5rem',
    fontWeight: 400,
    lineHeight: '2rem',
    color: tokens.colors.rsmGray.copy,
    marginBottom: '1rem',
  },
  message: {
    fontFamily: 'Prelo, sans-serif',
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: '1.5rem',
    color: 'text.secondary',
    marginBottom: '1rem',
  },
  userInfo: {
    fontFamily: 'Prelo, sans-serif',
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: '1.5rem',
    color: tokens.colors.rsmGray.copy,
  },
  actionsWrapper: {
    justifyContent: 'center',
    gap: '0.625rem',
    paddingTop: '1.5rem',
    paddingBottom: '0.5rem',
  },
}

/**
 * Confirmation dialog component for revoking all myRSM access.
 *
 * This component displays a critical confirmation dialog asking the user
 * to confirm removal of all myRSM access for a specific user. It shows
 * the user's full name and email to ensure the correct user is being
 * identified before the irreversible action is taken.
 *
 * @param props - Component props
 * @returns Confirmation dialog component
 */
const UserManagementConfirmRevokeAll = ({
  open,
  onClose,
  onConfirm,
  userFirstName,
  userLastName,
  userEmail,
}: UserManagementConfirmRevokeAllProps) => {
  const { t } = useTranslation()

  /**
   * Handles the confirm action by calling onConfirm callback
   * and then closing the dialog
   */
  const handleConfirm = () => {
    onConfirm()
    onClose()
  }

  return (
    <Box className="Rsm-ProfileModal">
      <ResponsiveModal
        open={open}
        onClose={onClose}
        aria-label={t('UserManagement.RevokeAccess.ConfirmTitle')}
        desktopWidth="44rem"
        desktopMaxWidth="56rem"
        desktopMinWidth="36rem"
        desktopMaxHeight="auto"
        desktopHeight="auto"
        desktopMinHeight="auto"
        removeHeaderSpacing
        data-testid="Dialog_RevokeAccess_Confirm">
        <Box
          role="alertdialog"
          aria-labelledby="revoke-dialog-title"
          aria-describedby="revoke-dialog-description"
          sx={styles.dialogContent}>
          <Typography
            id="revoke-dialog-title"
            data-testid="Lbl_RevokeAccess_Title"
            sx={styles.title}>
            {t('UserManagement.RevokeAccess.ConfirmTitle')}
          </Typography>

          <Typography
            id="revoke-dialog-description"
            data-testid="Lbl_RevokeAccess_Message"
            sx={styles.message}>
            {t('UserManagement.RevokeAccess.ConfirmMessage')}
          </Typography>

          <Typography
            data-testid="Lbl_RevokeAccess_UserInfo"
            sx={styles.userInfo}>
            {userFirstName} {userLastName} - {userEmail}
          </Typography>

          <Stack direction="row" sx={styles.actionsWrapper}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                fontFamily: 'Prelo, sans-serif',
                textTransform: 'none',
                minWidth: '7.5rem',
              }}
              data-testid="Btn_RevokeConfirm_Cancel">
              {t('UserManagement.RevokeAccess.CancelButton')}
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={handleConfirm}
              sx={{
                fontFamily: 'Prelo, sans-serif',
                textTransform: 'none',
                minWidth: '7.5rem',
              }}
              data-testid="Btn_RevokeConfirm_Remove">
              {t('UserManagement.RevokeAccess.ConfirmButton')}
            </Button>
          </Stack>
        </Box>
      </ResponsiveModal>
    </Box>
  )
}

export default UserManagementConfirmRevokeAll
